/**
 * Helpers for safely animating an SVG path `d` string with react-spring.
 *
 * react-spring's string interpolator (`AnimatedString`) tweens the numeric
 * tokens of two strings pairwise and throws
 * `Error: The arity of each "output" value must be equal`
 * when the `from` and `to` strings contain a different number of numeric
 * tokens. For an area chart the token count changes whenever the point count or
 * the `.defined()` gap structure changes, so we must only tween between
 * compatible paths and otherwise snap.
 */

const NUMBER_RE = /-?\d*\.?\d+(?:e[-+]?\d+)?/gi;

/** Number of interpolatable numeric tokens in an SVG path `d` string. */
export function countPathNumbers(d: string): number {
  return (d.match(NUMBER_RE) ?? []).length;
}

/** Command skeleton: the string with every numeric token replaced by `#`. */
export function pathSkeleton(d: string): string {
  return d.replace(NUMBER_RE, '#').replace(/\s+/g, ' ').trim();
}

/**
 * True only when `from` and `to` can be safely tweened by react-spring's string
 * interpolator: same numeric-token count (this is what react-spring throws on)
 * AND identical command skeleton (so the tween is also visually meaningful).
 */
export function canInterpolatePaths(from: string, to: string): boolean {
  if (!from || !to) return false;
  return (
    countPathNumbers(from) === countPathNumbers(to) &&
    pathSkeleton(from) === pathSkeleton(to)
  );
}
